/**
 * SIL-954 — spec-distance weighting for MCMP comp aggregation.
 *
 * `recomputeMcmp` (apps/ingestion/src/pipeline/mcmp.ts) groups comps by
 * (sku_id, condition_band) only. Two live consequences that this module
 * exists to correct, in the WEIGHTING math rather than the grouping key
 * (see the module-level doc on `recomputeMcmp` for why grouping is
 * explicitly rejected):
 *
 *   1. Spec variance is pooled — a 256GB and a 2TB build of the same SKU
 *      share one median with no signal that they are different machines.
 *   2. The master rollup double-counts — a variant's comps appear in both
 *      its own MCMP and the master rollup, and nothing corrects for one
 *      variant simply having more comps than its siblings.
 *
 * `computeSpecDistance()` compares a comp's `market_comp.as_built_config`
 * (SIL-856/865, @siliconx/shared normalizeAsBuiltConfig()) against its own
 * matched SKU's `sku.class_attributes` (the catalog's capability envelope)
 * and returns a normalized distance in [0, 1] across whichever axes are
 * present on BOTH sides — `null` when nothing is comparable (either side
 * has no usable data for any shared axis). `specDistanceToWeight()` turns
 * that into a per-comp weight: 1 (full weight) when nothing is comparable
 * — this is the "equal weighting as fallback" the ticket requires, applied
 * whenever `as_built_config` is null OR class_attributes carries none of
 * the axes as_built_config states — down to a floor that discounts a
 * heavily-mismatched comp WITHOUT ever excluding it (weight is never 0).
 *
 * ## Key-fold on BOTH sides — required, not defensive
 *
 * `db.ts`'s `CamelCasePlugin` has no `maintainNestedObjectKeys`, so it
 * recursively camelCases every nested JSONB key on the way OUT of Postgres
 * (see `backfill-market-comp-as-built-config.ts`'s `canonicalizeForCompare()`
 * doc comment, and `market-comp-admission.ts`'s `extractSelfReportSpecs()` —
 * this is an established, load-bearing pattern in this codebase, not new
 * here). `normalizeAsBuiltConfig()` always WRITES snake_case
 * (`capacity_gb`), but by the time a Kysely `SELECT` hands it to
 * `recomputeMcmp`, both `market_comp.as_built_config` (nested: `memory.
 * capacity_gb`) AND `sku.class_attributes` (flat: `installed_ram_gb`) come
 * back camelCased (`memory.capacityGb`, `installedRamGb`). Every key lookup
 * below therefore folds (strips non-alphanumerics, lowercases) BOTH the
 * candidate key names and the actual object keys before comparing — the
 * same discipline `normalize-as-built-config.ts`'s `foldKey()` and
 * `backfill-market-comp-as-built-config.ts`'s `canonicalizeForCompare()`
 * already use — so `capacity_gb`, `capacityGb`, and `CapacityGB` all match
 * the same axis regardless of which side of a Kysely round-trip produced
 * the object.
 *
 * ## Axes compared
 *
 * `sku.class_attributes` is a loose, per-manufacturer-import JSONB blob
 * (040_sku_master.sql) with no single canonical key set across the
 * catalog — e.g. rack-scale imports use `cpu_count`/`total_gpu_memory_gb`,
 * server imports use `installed_ram_gb`, GPU imports use `gpu_model` only.
 * Each axis below therefore tries a small set of observed synonym keys
 * (folded, see above) and uses the first one present. A SKU whose
 * class_attributes carries none of an axis's known keys simply does not
 * contribute that axis to the distance — it is not treated as a mismatch.
 *
 * ## Explicitly out of scope
 *
 * `region_id` — SIL-954's issue body: 0.0% populated in prod, not a
 * comparison axis, not a grouping key. Not touched by this module.
 */

/** Loosely-typed — see the "key-fold on BOTH sides" doc above for why this is never a strict nested interface. */
export type AsBuiltConfigLike = Record<string, unknown>;

/** Case-fold + de-space, same discipline as normalize-as-built-config.ts's foldKey(). */
function fold(s: string): string {
  return s.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/**
 * Find the first entry of `obj` whose key, folded, matches one of
 * `foldedTargets` (already-folded). Returns the raw value, untyped.
 */
function findFolded(obj: Record<string, unknown>, foldedTargets: readonly string[]): unknown {
  for (const [k, v] of Object.entries(obj)) {
    if (foldedTargets.includes(fold(k))) return v;
  }
  return undefined;
}

/**
 * Descend into `obj[groupNames][fieldNames]` with fold-tolerant matching at
 * both levels — e.g. `getNested(config, ['memory'], ['capacity_gb'])` finds
 * `config.memory.capacity_gb` OR `config.memory.capacityGb` OR
 * `config.Memory.CapacityGb`, etc.
 */
function getNested(obj: unknown, groupNames: readonly string[], fieldNames: readonly string[]): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  const group = findFolded(obj as Record<string, unknown>, groupNames.map(fold));
  if (!group || typeof group !== 'object') return undefined;
  return findFolded(group as Record<string, unknown>, fieldNames.map(fold));
}

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function asNonEmptyString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

/** First numeric value found in a FLAT object under any of `foldedKeys` (already-folded target names). */
function firstNumericFlat(attrs: Record<string, unknown>, foldedKeys: readonly string[]): number | undefined {
  return asNumber(findFolded(attrs, foldedKeys));
}

/** Relative distance between two positive magnitudes, capped at 1 (i.e. "off by 100% or more" all reads the same). */
function relativeNumericDistance(a: number, b: number): number {
  const denom = Math.max(Math.abs(a), Math.abs(b), 1e-9);
  return Math.min(Math.abs(a - b) / denom, 1);
}

/** Fold-normalized substring match either direction — "Gold 5420+" vs "Xeon Gold 5420+" is a match. */
function stringsMatch(a: string, b: string): boolean {
  const fa = fold(a);
  const fb = fold(b);
  if (fa === fb) return true;
  return fa.length > 0 && fb.length > 0 && (fa.includes(fb) || fb.includes(fa));
}

const RAM_KEYS = ['installed_ram_gb', 'ram_gb', 'memory_gb', 'max_ram_gb'].map(fold);
const CPU_MODEL_KEYS = ['cpu_model'].map(fold);
const CPU_SOCKET_KEYS = ['cpu_count', 'cpu_sockets', 'cpu_socket_count'].map(fold);
const STORAGE_KEYS = ['storage_gb', 'storage_capacity_gb', 'total_storage_gb'].map(fold);
const GPU_COUNT_KEYS = ['gpu_count'].map(fold);
const GPU_MEMORY_KEYS = ['total_gpu_memory_gb', 'gpu_memory_gb', 'vram_gb'].map(fold);
const POWER_SUPPLY_WATTS_KEYS = ['power_supply_watts', 'psu_watts'].map(fold);

/**
 * Compare a comp's `as_built_config` against its matched SKU's
 * `class_attributes`. Returns the mean per-axis distance (each axis
 * normalized to [0, 1]) across every axis present on BOTH sides, or `null`
 * when no axis is comparable (equal-weight fallback territory — see
 * `specDistanceToWeight()`).
 */
export function computeSpecDistance(
  asBuiltConfig: AsBuiltConfigLike | null | undefined,
  classAttributes: Record<string, unknown> | null | undefined,
): number | null {
  if (!asBuiltConfig || typeof asBuiltConfig !== 'object') return null;
  const attrs = classAttributes && typeof classAttributes === 'object' ? classAttributes : {};

  const distances: number[] = [];

  const configRam = asNumber(getNested(asBuiltConfig, ['memory'], ['capacity_gb']));
  if (configRam !== undefined) {
    const skuRam = firstNumericFlat(attrs, RAM_KEYS);
    if (skuRam !== undefined) distances.push(relativeNumericDistance(configRam, skuRam));
  }

  const configCpuModel = asNonEmptyString(getNested(asBuiltConfig, ['cpu'], ['model']));
  if (configCpuModel !== undefined) {
    const skuCpuModel = asNonEmptyString(findFolded(attrs, CPU_MODEL_KEYS));
    if (skuCpuModel !== undefined) distances.push(stringsMatch(configCpuModel, skuCpuModel) ? 0 : 1);
  }

  const configCpuSockets = asNumber(getNested(asBuiltConfig, ['cpu'], ['socket_count']));
  if (configCpuSockets !== undefined) {
    const skuCpuSockets = firstNumericFlat(attrs, CPU_SOCKET_KEYS);
    if (skuCpuSockets !== undefined) distances.push(relativeNumericDistance(configCpuSockets, skuCpuSockets));
  }

  const configStorage = asNumber(getNested(asBuiltConfig, ['storage'], ['total_capacity_gb']));
  if (configStorage !== undefined) {
    const skuStorage = firstNumericFlat(attrs, STORAGE_KEYS);
    if (skuStorage !== undefined) distances.push(relativeNumericDistance(configStorage, skuStorage));
  }

  const configGpuCount = asNumber(getNested(asBuiltConfig, ['gpu'], ['count']));
  if (configGpuCount !== undefined) {
    const skuGpuCount = firstNumericFlat(attrs, GPU_COUNT_KEYS);
    if (skuGpuCount !== undefined) distances.push(relativeNumericDistance(configGpuCount, skuGpuCount));
  }

  const configGpuMemory = asNumber(getNested(asBuiltConfig, ['gpu'], ['memory_gb']));
  if (configGpuMemory !== undefined) {
    const skuGpuMemory = firstNumericFlat(attrs, GPU_MEMORY_KEYS);
    if (skuGpuMemory !== undefined) distances.push(relativeNumericDistance(configGpuMemory, skuGpuMemory));
  }

  const configPsuWatts = asNumber(getNested(asBuiltConfig, ['power_supply'], ['watts']));
  if (configPsuWatts !== undefined) {
    const skuPsuWatts = firstNumericFlat(attrs, POWER_SUPPLY_WATTS_KEYS);
    if (skuPsuWatts !== undefined) distances.push(relativeNumericDistance(configPsuWatts, skuPsuWatts));
  }

  if (distances.length === 0) return null;
  return distances.reduce((s, d) => s + d, 0) / distances.length;
}

/**
 * Distance -> per-comp weight. `distance === null` (no comparable axis —
 * `as_built_config` is null, or class_attributes states none of the axes
 * as_built_config does) maps to weight 1 — the equal-weight fallback the
 * ticket requires: "degrade gracefully, never exclude."
 *
 * For a comparable distance, `WEIGHT_SPREAD` controls how hard a full
 * mismatch (distance = 1) gets discounted. A floor of 1 / (1 + SPREAD)
 * keeps every comp contributing something — weighting, never grouping,
 * never zeroing a comp out — while still meaningfully separating "close to
 * this SKU's envelope" from "wildly different config" comps sharing one
 * (sku_id, condition_band) bucket.
 */
const WEIGHT_SPREAD = 3;

export function specDistanceToWeight(distance: number | null): number {
  if (distance === null) return 1;
  const clamped = Math.max(0, Math.min(1, distance));
  return 1 / (1 + WEIGHT_SPREAD * clamped);
}

/** Convenience: distance + weight in one call for the common call site. */
export function computeSpecWeight(
  asBuiltConfig: AsBuiltConfigLike | null | undefined,
  classAttributes: Record<string, unknown> | null | undefined,
): number {
  return specDistanceToWeight(computeSpecDistance(asBuiltConfig, classAttributes));
}
